import React, { useState, useEffect } from 'react';
import useFetch from 'use-http';
import _ from 'lodash';
import Loader from '../Loader';
import GroupedSearchResults from './GroupedSearchResults';
import Select from '@/v2/components/Select.jsx';
import { SearchIcon } from 'lucide-react';
import useGAEventsTracker from '@/components/hooks/useGAEventsTracker';

const searchInputReplaceRegex = /[^a-zA-Z0-9ąčęėįšųūžĄČĘĖĮŠŲŪŽ\s():-]/g;

const Search = ({ onSearchResults }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [results, setResults] = useState({});
  const [lastQuery, setLastQuery] = useState('');
  const [allowShowEmptyResults, setAllowShowEmptyResults] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const {
    get: fetchSearchResults,
    response: fetchSearchResultsResponse,
    loading: fetchSearchResultsLoading,
  } = useFetch('/Services/ServicesBySearch');

  const { get: getCitySelector } = useFetch(
    '/UserSearchManagement/citySelector',
  );

  const trackEvent = useGAEventsTracker('Pagrindinis puslapis');

  useEffect(() => {
    const loadCities = async () => {
      const cities = await getCitySelector();
      if (cities) {
        setCityOptions(cities);
      }
    };

    loadCities();
  }, [getCitySelector]);

  useEffect(() => {
    setAllowShowEmptyResults(false);

    const timer = setTimeout(() => {
      if (!searchTerm) {
        setResults({});
        if (onSearchResults) {
          onSearchResults({
            query: '',
            results: {},
            selectedCity: selectedCity?.original,
          });
        }
        return;
      }

      const replacedQuery = searchTerm.replace(searchInputReplaceRegex, '');

      if (!replacedQuery || replacedQuery.trim().length < 3) {
        setResults({});
        return;
      }

      setLastQuery(replacedQuery);

      trackEvent('Paieška', replacedQuery, {
        search_term: replacedQuery,
        city_id: selectedCity?.original?.cityId || null,
        city_name: selectedCity?.original?.cityName || null,
      });

      const encodedQuery = encodeURIComponent(replacedQuery);
      const queryParams = [`Search=${encodedQuery}`];

      if (selectedCity?.original?.cityId) {
        queryParams.push(`cityId=${selectedCity.original.cityId}`);
      }

      fetchSearchResults(`?${queryParams.join('&')}`).then((res) => {
        if (!fetchSearchResultsResponse.ok) {
          setResults({});
          return;
        }

        setAllowShowEmptyResults(true);

        const grouped = replacedQuery ? _.groupBy(res, 'serviceTypeTitle') : {};

        setResults(grouped);

        if (onSearchResults) {
          onSearchResults({
            query: replacedQuery,
            results: grouped,
            selectedCity: selectedCity?.original,
          });
        }
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedCity]);

  const shouldShowResults =
    isFocused &&
    searchTerm.length >= 3 &&
    !fetchSearchResultsLoading &&
    (Object.keys(results).length > 0 || allowShowEmptyResults);

  return (
    <div className="w-full font-primary">
      <div className="flex flex-col md:flex-row gap-3 w-full">
        <Select
          selected={selectedCity}
          onChange={setSelectedCity}
          options={cityOptions?.map((c) => ({
            label: c.cityName,
            value: c.cityId,
            original: c,
          }))}
          placeholder="Visi miestai"
          className="w-full md:basis-1/3"
        />

        <div className="relative w-full md:basis-2/3">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <SearchIcon size={20} className="text-[#1F4B81]" />
          </div>

          <input
            type="text"
            placeholder="Įrašykite paslaugą arba gydytoją..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`pl-10 pr-20 py-3 border border-gray-500 text-sm w-full bg-white ${
              shouldShowResults
                ? 'rounded-t-[1.4rem] rounded-b-none border-gray-500'
                : 'rounded-full'
            } focus:outline-none focus:ring-0`}
          />

          {(searchTerm || fetchSearchResultsLoading) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {fetchSearchResultsLoading && (
                <div className="w-5 h-5 flex items-center justify-center">
                  <Loader inline size={20} light />
                </div>
              )}
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  &#10005;
                </button>
              )}
            </div>
          )}

          {shouldShowResults && (
            <GroupedSearchResults results={results} lastQuery={lastQuery} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
